import { Component, DestroyRef, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { DateAdapter } from '@angular/material/core';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { PricePipe } from '@/common/pipes/price.pipe';
import { FormatDateTimePipe } from '@/common/pipes/format-date-time.pipe';
import { DateService } from '@/common/date.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { CURRENCIES } from '@/common/constants/currencies';
import { getPolishPaginatorIntl } from '@/common/get-polish-paginator-intl';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, finalize } from 'rxjs';
import { DriverLedgerApiService } from '../_api/driver-ledger.api.service';
import {
	DriverLedgerCategoryOption,
	DriverLedgerEntry,
	DriverLedgerFilters,
	DriverLedgerFormDialogData,
	DriverLedgerFormDialogResult,
	DriverLedgerSummary,
	LEDGER_ENTRY_STATUSES,
	LEDGER_ENTRY_STATUS_LABEL,
	LedgerEntryStatus,
} from '../_models/driver-ledger.model';
import { DriverLedgerFormComponent } from '../driver-ledger-form/driver-ledger-form.component';
import { DriverLedgerDeleteConfirmComponent } from './driver-ledger-delete-confirm.component';

@Component({
	selector: 'app-driver-ledger-list',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatCardModule,
		MatTableModule,
		MatButtonModule,
		MatProgressSpinnerModule,
		MatPaginatorModule,
		MatIconModule,
		MatTooltipModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatDatepickerModule,
		MatChipsModule,
		MatAutocompleteModule,
		DecimalPipe,
		PricePipe,
		FormatDateTimePipe,
	],
	providers: [
		{ provide: DateAdapter, useClass: PolishDateAdapter },
		{ provide: MatPaginatorIntl, useValue: getPolishPaginatorIntl() },
	],
	templateUrl: './driver-ledger-list.component.html',
	styleUrl: './driver-ledger-list.component.scss',
})
export class DriverLedgerListComponent implements OnInit {
	driverId = input.required<string>();

	table = viewChild<MatTable<DriverLedgerEntry>>('dataTable');
	paginator = viewChild(MatPaginator);
	matData = new MatTableDataSource<DriverLedgerEntry>([]);

	readonly displayedColumns = [
		'expand',
		'date',
		'amount',
		'status',
		'category',
		'vehicle',
		'hasOriginalDocument',
		'description',
		'paymentDate',
		'actions',
	];
	readonly statuses = LEDGER_ENTRY_STATUSES;
	readonly statusLabel: Record<string, string> = LEDGER_ENTRY_STATUS_LABEL;

	private static readonly FORM_DIALOG_CONFIG = {
		width: 'min(1100px, 95vw)',
		maxWidth: '95vw',
		panelClass: 'driver-ledger-form-dialog',
	} as const;

	entries = signal<DriverLedgerEntry[]>([]);
	expandedEntry = signal<DriverLedgerEntry | null>(null);
	summary = signal<DriverLedgerSummary | null>(null);
	isLoading = signal(false);
	totalItems = signal(0);
	currentPage = signal(1);
	pageSize = signal(50);

	filterForm!: FormGroup;
	readonly currencies = CURRENCIES;
	readonly categories = signal<DriverLedgerCategoryOption[]>([]);
	readonly filteredCategories = signal<DriverLedgerCategoryOption[]>([]);
	readonly categoryControl = new FormControl<DriverLedgerCategoryOption | string | null>(null);

	private readonly apiService = inject(DriverLedgerApiService);
	private readonly dialog = inject(MatDialog);
	private readonly fb = inject(FormBuilder);
	private readonly dateService = inject(DateService);
	private readonly infobox = inject(InfoboxService);
	private readonly destroyRef = inject(DestroyRef);

	get hasActiveFilters(): boolean {
		const v = this.filterForm.value;
		return !!v.dateFrom || !!v.dateTo || !!v.currency || !!v.categoryId || !!v.status;
	}

	ngOnInit(): void {
		this.initFilterForm();
		this.apiService
			.getCategories()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((categories) => {
				this.categories.set(categories);
				this.filteredCategories.set(categories);
			});
		this.categoryControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
			const query = typeof value === 'string' ? value.toLowerCase() : '';
			this.filteredCategories.set(
				query ? this.categories().filter((c) => c.name.toLowerCase().includes(query)) : this.categories(),
			);
			const categoryId = value && typeof value === 'object' ? value.id : '';
			this.filterForm.patchValue({ categoryId }, { emitEvent: true });
		});
		this.filterForm.valueChanges
			.pipe(
				debounceTime(400),
				distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => this.onFilter());
		this.loadData();
	}

	loadData(): void {
		this.loadEntries();
		this.loadSummary();
	}

	loadEntries(): void {
		this.isLoading.set(true);

		const filters: DriverLedgerFilters = {
			page: this.currentPage(),
			perPage: this.pageSize(),
		};

		const formValue = this.filterForm.value;
		if (formValue.dateFrom) {
			filters.dateFrom = this.formatDate(formValue.dateFrom);
		}
		if (formValue.dateTo) {
			filters.dateTo = this.formatDate(formValue.dateTo);
		}
		if (formValue.currency) {
			filters.currency = formValue.currency;
		}
		if (formValue.categoryId) {
			filters.categoryId = formValue.categoryId;
		}
		if (formValue.status) {
			filters.status = formValue.status as LedgerEntryStatus;
		}

		const driverId = this.driverId();
		if (!driverId) {
			this.isLoading.set(false);
			return;
		}

		this.apiService
			.getLedger(driverId, filters)
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe((response) => {
				this.entries.set(response.items ?? []);
				this.totalItems.set(response.total);
				this.refreshTable();
			});
	}

	loadSummary(): void {
		const driverId = this.driverId();
		if (!driverId) return;

		this.apiService.getSummary(driverId).subscribe((summary) => {
			this.summary.set(summary);
		});
	}

	onFilter(): void {
		this.currentPage.set(1);
		this.loadData();
	}

	onClearFilters(): void {
		this.categoryControl.setValue(null, { emitEvent: false });
		this.filterForm.reset({
			dateFrom: null,
			dateTo: null,
			currency: '',
			categoryId: '',
			status: '',
		});
		this.currentPage.set(1);
		this.loadData();
	}

	onPageChange(event: PageEvent): void {
		this.currentPage.set(event.pageIndex + 1);
		this.pageSize.set(event.pageSize);
		this.loadEntries();
	}

	openCreateDialog(): void {
		const dialogRef = this.dialog.open(DriverLedgerFormComponent, {
			...DriverLedgerListComponent.FORM_DIALOG_CONFIG,
			data: { driverId: this.driverId() } as DriverLedgerFormDialogData,
		});

		dialogRef.afterClosed().subscribe((result: DriverLedgerFormDialogResult | null) => {
			if (result?.action !== 'create') return;
			this.apiService
				.createEntry(this.driverId(), result.payload)
				.pipe(
					catchError(() => {
						this.infobox.error('Błąd podczas dodawania wpisu');
						return EMPTY;
					}),
				)
				.subscribe(() => {
					this.infobox.success('Wpis został dodany');
					this.loadData();
				});
		});
	}

	openEditDialog(entry: DriverLedgerEntry): void {
		if (!entry.isMine) {
			return;
		}

		const dialogRef = this.dialog.open(DriverLedgerFormComponent, {
			...DriverLedgerListComponent.FORM_DIALOG_CONFIG,
			data: { driverId: this.driverId(), entry } as DriverLedgerFormDialogData,
		});

		dialogRef.afterClosed().subscribe((result: DriverLedgerFormDialogResult | null) => {
			if (result?.action !== 'update') return;
			this.apiService
				.updateEntry(this.driverId(), entry.id, result.payload)
				.pipe(
					catchError((err: { status?: number }) => {
						const msg =
							err?.status === 403
								? 'Tylko autor może edytować ten wpis'
								: 'Błąd podczas aktualizacji wpisu';
						this.infobox.error(msg);
						return EMPTY;
					}),
				)
				.subscribe(() => {
					this.infobox.success('Wpis został zaktualizowany');
					this.loadData();
				});
		});
	}

	deleteEntry(entry: DriverLedgerEntry, event: Event): void {
		event.stopPropagation();

		if (!entry.isMine) {
			this.infobox.error('Tylko autor może usunąć ten wpis');
			return;
		}

		const dialogRef = this.dialog.open(DriverLedgerDeleteConfirmComponent, {
			width: '400px',
		});

		dialogRef.afterClosed().subscribe((confirmed: boolean) => {
			if (!confirmed) return;
			this.apiService
				.deleteEntry(this.driverId(), entry.id)
				.pipe(
					catchError((err: { status?: number }) => {
						const msg =
							err?.status === 403 ? 'Tylko autor może usunąć ten wpis' : 'Błąd podczas usuwania wpisu';
						this.infobox.error(msg);
						return EMPTY;
					}),
				)
				.subscribe(() => {
					this.infobox.success('Wpis został usunięty');
					this.loadData();
				});
		});
	}

	toggleOriginalDocument(entry: DriverLedgerEntry, event: Event): void {
		event.stopPropagation();

		const nextValue = !entry.hasOriginalDocument;
		this.applyOriginalDocument(entry.id, nextValue);

		this.apiService
			.toggleOriginalDocument(this.driverId(), entry.id, nextValue)
			.pipe(
				catchError(() => {
					this.applyOriginalDocument(entry.id, !nextValue);
					this.infobox.error('Błąd podczas aktualizacji oryginału');
					return EMPTY;
				}),
			)
			.subscribe();
	}

	toggleExpand(entry: DriverLedgerEntry, event: Event): void {
		event.stopPropagation();
		this.expandedEntry.update((current) => (current?.id === entry.id ? null : entry));
	}

	isExpanded(entry: DriverLedgerEntry): boolean {
		return this.expandedEntry()?.id === entry.id;
	}

	displayCategory(category: DriverLedgerCategoryOption | null): string {
		return category ? category.name : '';
	}

	private applyOriginalDocument(entryId: number, value: boolean): void {
		this.entries.update((rows) =>
			rows.map((row) => (row.id === entryId ? { ...row, hasOriginalDocument: value } : row)),
		);
		this.refreshTable();
	}

	private initFilterForm(): void {
		this.filterForm = this.fb.group({
			dateFrom: [null],
			dateTo: [null],
			currency: [''],
			categoryId: [''],
			status: [''],
		});
	}

	private formatDate(date: Date | string): string {
		if (typeof date === 'string') {
			return date.split(' ')[0];
		}
		return this.dateService.datetimeWithTimezone(date).date;
	}

	private refreshTable(): void {
		this.matData.data = this.entries();
		const tableInstance = this.table();
		if (tableInstance) {
			tableInstance.renderRows();
		}
	}
}
